import Matter from 'matter-js';
import decomp from 'poly-decomp';
// Explicit CJS entry (see the `pixi-cjs` webpack rule in vue.config.js) — importing the
// bare 'pixi.js' specifier here would still resolve to its ESM build regardless of what
// that rule forces for files *inside* node_modules, leaving this file's own top-level
// import mismatched against the CJS-shaped @pixi/* packages it then pulls in.
import * as PIXI from 'pixi.js/dist/cjs/pixi.js';
import Widget from './Widget';
import './banReaperWidget.scss';

Matter.Common.setDecomp(decomp);
const { Engine, Composite, Bodies, Body } = Matter;

const WALL = 40;
const BALL_R = 46;
const REAPER_SIZE = 120;
const LINGER_MS = 1800;
const CHATTER_FADE_MS = 350;
const DROP_R = 12;
const STEP = 1000 / 30;
const GOO_BLUR_STRENGTH = 16;
const BLOOD_COLOR = [0.702, 0.059, 0.082, 1]; // #b30f15

const AVATAR_COLORS = [
	'#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#1abc9c',
	'#3498db', '#9b59b6', '#e91e63', '#00bcd4', '#ff5722',
];

// Reproduces the original SVG gooey filter exactly:
//   <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur"/>
//   <feColorMatrix in="blur" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 26 -7" result="goo"/>
//   <feBlend in="SourceGraphic" in2="goo"/>
// — blur the droplets, run the SAME hard `26*a-7` alpha remap SVG used (not a softened
// smoothstep — that's what made this look softer/mushier than the reference), and finally
// blend the untouched sharp circles back on top of that thresholded pool (`_drawSolids`
// below draws the raw droplet texture over the goo texture) so individual droplets keep
// crisp edges and only the connective "webbing" between them is the blurred/thresholded part.
// Done with PIXI's own multi-pass BlurFilter rather than a hand-rolled tap shader — a
// hand-rolled few-tap blur kept aliasing into visible spikes or a scalloped edge no matter
// how the taps were spaced.
const GOO_THRESHOLD_FRAGMENT_SHADER = `
	varying vec2 vTextureCoord;
	uniform sampler2D uSampler;
	uniform vec4 uColor;

	void main(void) {
		float a = texture2D(uSampler, vTextureCoord).a;
		float a2 = clamp(a * 26.0 - 7.0, 0.0, 1.0);
		gl_FragColor = vec4(uColor.rgb * a2, a2);
	}
`;

function hashCode(str) {
	let h = 0;
	for(let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
	return Math.abs(h);
}

// true area centroid of a polygon (Matter re-centers a body here, so we must match it
// or the mesh's UVs drift off the texture it's supposed to be cut from)
function polyCentroid(pts) {
	let a = 0, cx = 0, cy = 0;
	for(let i = 0; i < pts.length; i++) {
		const p = pts[i], q = pts[(i + 1) % pts.length];
		const cross = p.x * q.y - q.x * p.y;
		a += cross; cx += (p.x + q.x) * cross; cy += (p.y + q.y) * cross;
	}
	a *= 0.5;
	return { x: cx / (6 * a), y: cy / (6 * a) };
}

// one side of a straight cut: the disk intersected with a half-plane (always convex).
function segmentVerts(R, phi, d, side) {
	const beta = Math.acos(Math.max(-1, Math.min(1, d / R)));
	const a0 = side > 0 ? phi - beta : phi + beta;
	const a1 = side > 0 ? phi + beta : phi - beta + 2 * Math.PI;
	const N = 30, v = [];
	for(let i = 0; i <= N; i++) {
		const a = a0 + (a1 - a0) * i / N;
		v.push({ x: R * Math.cos(a), y: R * Math.sin(a) });
	}
	return v;
}

function formatDuration(seconds) {
	seconds = parseInt(seconds) || 0;
	if(seconds <= 0) return null;
	if(seconds < 60) return `${seconds}s timeout`;
	if(seconds < 3600) return `${Math.round(seconds / 60)}m timeout`;
	return `${Math.round(seconds / 3600)}h timeout`;
}

function formatKillCount(n) {
	return n === 1 ? '1 kill' : `${n} kills`;
}

// Where the sprite's actually-visible (trimmed) pixels start/end vertically, in its
// parent's local space — not the padded, untrimmed source frame. `sprite.height`/
// `getLocalBounds()` both report the untrimmed box, so labels anchored off those can end
// up floating far from a frame whose real content only fills part of it.
function trimmedVerticalExtent(sprite) {
	const tex = sprite.texture;
	const orig = tex && tex.orig;
	if(!orig || !orig.height) return { top: 0, bottom: 0 };
	const trim = tex.trim;
	const top = trim ? trim.y : 0;
	const bottom = trim ? trim.y + trim.height : orig.height;
	const anchorOffset = sprite.anchor.y * orig.height;
	return {
		top: (top - anchorOffset) * sprite.scale.y,
		bottom: (bottom - anchorOffset) * sprite.scale.y,
	};
}

export default class BanReaperWidget extends Widget {
	constructor({ container }) {
		super({ container });

		this.app = null;
		this.engine = null;
		this.world = null;
		this.W = 0;
		this.H = 0;
		this.FLOOR_Y = 0;
		this.blurEnabled = true;

		this.walls = [];
		this.halves = [];
		this.drops = [];
		this.dropPool = [];

		// Single-slot playback: one avatar, one reaper, ever, plus a FIFO of what's
		// still waiting. A moderator with several queued bans plays them all on the one
		// reaper appearance (see onBanEvent/_advanceQueue) instead of each getting its own.
		this.queue = [];
		this.activeAvatar = null;
		this.activeReaper = null;
		this._startingReaper = false;

		// Resolved {texture,time}[] frame arrays, cached by metaUrl so repeat moderators
		// don't re-fetch/re-parse their spritesheet on every ban.
		this._sheetCache = new Map();
		this._placeholder = null;

		// Per-moderator kill totals, keyed the same way as onBanEvent's `key` (moderator
		// id/name, or 'unknown' for an unidentified/default moderator) — kept independent
		// of activeReaper so a count survives that moderator's reaper fading out and
		// reappearing later, instead of restarting at 0 every session.
		this._killCounts = new Map();

		this.MAX_DROPS = 900;
		this.bleedSeconds = 1;

		this._resizeObserver = null;
		this._animFrameId = null;
		this._lastTime = performance.now();
		this._acc = 0;
	}

	// ── Build ────────────────────────────────────────────────────────────────

	_build() {
		this.container.style.position = 'relative';
		this.container.style.overflow = 'hidden';

		this.W = Math.max(1, this.container.clientWidth || window.innerWidth);
		this.H = Math.max(1, this.container.clientHeight || window.innerHeight);
		this.FLOOR_Y = this.H - 8;

		this.app = new PIXI.Application({
			width: this.W, height: this.H,
			backgroundAlpha: 0,
			antialias: true,
			autoDensity: true,
			resolution: window.devicePixelRatio || 1,
			autoStart: false,
		});
		this.container.appendChild(this.app.view);

		// Explicit resolution: RenderTexture.create doesn't otherwise inherit the
		// renderer's own (devicePixelRatio-driven) resolution, and a mismatch there is
		// exactly what makes an upscaled texture look blocky/pixelated on a hi-DPI display.
		this.bloodRenderTexture = PIXI.RenderTexture.create({ width: this.W, height: this.H, resolution: this.app.renderer.resolution });
		this.bloodGraphics = new PIXI.Graphics();

		// Two sprites sharing the same raw-droplets texture, reproducing the SVG filter's
		// `feBlend in="SourceGraphic" in2="goo"` finish: the filtered one (blurred, then
		// hard-thresholded back to a pool) sits underneath as the connective "webbing", and
		// the same droplets again — unfiltered — sit on top so each one keeps a crisp edge
		// instead of the whole mass reading as one soft blob.
		this.bloodGooSprite = new PIXI.Sprite(this.bloodRenderTexture);
		this.gooBlurFilter = new PIXI.filters.BlurFilter(GOO_BLUR_STRENGTH, 4);
		this.gooThresholdFilter = new PIXI.Filter(undefined, GOO_THRESHOLD_FRAGMENT_SHADER, {
			uColor: BLOOD_COLOR,
		});
		this.bloodGooSprite.filters = [this.gooBlurFilter, this.gooThresholdFilter];
		this.bloodGooSprite.visible = this.blurEnabled;
		this.bloodRawSprite = new PIXI.Sprite(this.bloodRenderTexture);

		this.avatarLayer = new PIXI.Container();
		this.halvesLayer = new PIXI.Container();
		this.reaperLayer = new PIXI.Container();
		// Reaper first so the avatar (and its cut halves) always render on top of it.
		this.app.stage.addChild(this.bloodGooSprite, this.bloodRawSprite, this.reaperLayer, this.halvesLayer, this.avatarLayer);

		this.engine = Engine.create();
		this.engine.gravity.y = 1;
		this.world = this.engine.world;
		this._buildStatics();

		this._resizeObserver = new ResizeObserver(entries => {
			const { width, height } = entries[0].contentRect;
			this._resize(width, height);
		});
		this._resizeObserver.observe(this.container);

		this._lastTime = performance.now();
		this._animFrameId = requestAnimationFrame(t => this._tick(t));
	}

	_generateCSS() {}

	destroy() {
		if(this._animFrameId != null) { cancelAnimationFrame(this._animFrameId); this._animFrameId = null; }
		if(this._resizeObserver) { this._resizeObserver.disconnect(); this._resizeObserver = null; }
		if(this.engine) { Engine.clear(this.engine); this.engine = null; this.world = null; }
		// Never destroy textures/baseTextures here — they're cached globally by URL and
		// reused across widget instances (the settings-page preview tears this down and
		// rebuilds it on every settings change); only display objects are ours to clean up.
		if(this.app) { this.app.destroy(true, { children: true, texture: false, baseTexture: false }); this.app = null; }
		super.destroy();
	}

	// ── Settings ─────────────────────────────────────────────────────────────

	applySettings(settings) {
		super.applySettings(settings);
		const s = this.settings || {};

		this.MAX_DROPS = s.maxParticles > 0 ? s.maxParticles : 900;
		while(this.drops.length > this.MAX_DROPS) this._freeDrop(this.drops.shift());

		this.bleedSeconds = s.bleedSeconds != null && s.bleedSeconds !== '' ? parseFloat(s.bleedSeconds) : 1;

		this.blurEnabled = s.blurEnabled !== false;
		if(this.bloodGooSprite) this.bloodGooSprite.visible = this.blurEnabled;

		// Live-move/resize whichever avatar/reaper is currently showing so position and
		// size settings are visible in the preview immediately, not just on the next spawn.
		this._applyAvatarPosition();
		this._applyAvatarSize();
		this._applyReaperPosition();
		this._applyReaperSize();
		this._applyIdlePingPong();

		this._updateFontLink();
	}

	// weight+fallback-size in, a PIXI.TextStyle out — `settings.fontSize` (when set)
	// overrides every text element uniformly rather than each keeping its own size.
	_textStyle(weight, defaultSize, fillColor, outlineColor) {
		const s = this.settings || {};
		const size = parseFloat(s.fontSize) > 0 ? parseFloat(s.fontSize) : defaultSize;
		const spread = parseFloat(s.spreadRadius) || 0;
		const blur = parseFloat(s.blurRadius) || 0;
		return new PIXI.TextStyle({
			fontFamily: s.fontFamily || 'ui-sans-serif, -apple-system, sans-serif',
			fontSize: size,
			fontWeight: weight,
			fill: fillColor,
			align: 'center',
			stroke: outlineColor || 'rgba(0, 0, 0, 1)',
			strokeThickness: spread,
			dropShadow: blur > 0,
			dropShadowBlur: blur,
			dropShadowDistance: 0,
			dropShadowAlpha: 1,
			dropShadowColor: outlineColor || 'rgba(0, 0, 0, 1)',
		});
	}

	// ── Reaper image resolution ──────────────────────────────────────────────

	_findModerator(bannedBy) {
		if(!bannedBy) return null;
		const mods = this.settings?.moderators || [];
		return mods.find(m => {
			if(m.id && bannedBy.id != null && String(m.id) === String(bannedBy.id)) return true;
			const bName = bannedBy.displayName || bannedBy.username;
			return !!(m.username && bName && m.username.toLowerCase() === String(bName).toLowerCase());
		}) || null;
	}

	async _resolveReaperSheets(bannedBy) {
		const s = this.settings || {};
		const match = this._findModerator(bannedBy);
		const name = bannedBy?.displayName || bannedBy?.username || null;

		const idleImg = (match?.reaperImage?.metaUrl ? match.reaperImage : null) || s.defaultReaperImage;
		const slashImg = (match?.reaperSlashImage?.metaUrl ? match.reaperSlashImage : null) || s.defaultReaperSlashImage || idleImg;

		const idle = idleImg?.metaUrl ? await this._loadSheet(idleImg) : this._placeholderSheet();
		const slash = slashImg?.metaUrl ? await this._loadSheet(slashImg) : idle;

		return { idle, slash, name };
	}

	// Resolved fresh from `bannedBy` (rather than frozen at spawn time) so editing a
	// moderator's offset/size in settings moves the reaper that's already on screen.
	_reaperGeometry(bannedBy) {
		const s = this.settings || {};
		const mod = this._findModerator(bannedBy);
		// Blank/unset (not 0 — 0 is a meaningful "cut immediately") falls through to the
		// moderator's own value, then the global default, then null (cut at animation end).
		const parseDelay = raw => (raw === '' || raw == null) ? null : parseFloat(raw);
		return {
			reaperSize: parseFloat(mod?.reaperSize) || parseFloat(s.reaperSize) || REAPER_SIZE,
			offsetX: parseFloat(mod?.offsetX) || 0,
			offsetY: parseFloat(mod?.offsetY) || 0,
			slashCutDelay: parseDelay(mod?.slashCutDelay) ?? parseDelay(s.slashCutDelay),
		};
	}

	_loadSheet(imgObj) {
		if(!this._sheetCache.has(imgObj.metaUrl)) this._sheetCache.set(imgObj.metaUrl, this._buildSheet(imgObj));
		return this._sheetCache.get(imgObj.metaUrl);
	}

	async _buildSheet(imgObj) {
		const meta = await fetch(imgObj.metaUrl).then(r => r.json());
		const baseTexture = PIXI.BaseTexture.from(imgObj.atlasUrl);
		const sheet = new PIXI.Spritesheet(baseTexture, meta);
		const textures = await sheet.parse();
		return meta.frameOrder.map((name, i) => ({ texture: textures[name], time: meta.frameDurations[i] || 100 }));
	}

	// Zero-config fallback so the widget isn't silently blank before anything's uploaded.
	_placeholderSheet() {
		if(this._placeholder) return this._placeholder;
		const size = 128;
		const canvas = document.createElement('canvas');
		canvas.width = size; canvas.height = size;
		const ctx = canvas.getContext('2d');
		ctx.font = `${Math.round(size * 0.7)}px serif`;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText('💀', size / 2, size / 2 + size * 0.05);
		this._placeholder = [{ texture: PIXI.Texture.from(canvas), time: 1000 }];
		return this._placeholder;
	}

	// ── Avatar textures ──────────────────────────────────────────────────────

	_avatarFallbackTexture(name) {
		const size = 92;
		const canvas = document.createElement('canvas');
		canvas.width = size; canvas.height = size;
		const ctx = canvas.getContext('2d');
		const initials = (name || '?').slice(0, 2).toUpperCase();
		ctx.fillStyle = AVATAR_COLORS[hashCode(name || '') % AVATAR_COLORS.length];
		ctx.beginPath(); ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2); ctx.fill();
		ctx.fillStyle = 'white';
		ctx.font = 'bold 36px Arial';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText(initials, size / 2, size / 2 + 2);
		return PIXI.Texture.from(canvas);
	}

	_applyAvatarTexture(sprite, url, name) {
		if(!url) { sprite.texture = this._avatarFallbackTexture(name); return; }
		const texture = PIXI.Texture.from(url);
		sprite.texture = texture;
		if(!texture.baseTexture.valid) {
			texture.baseTexture.once('error', () => { sprite.texture = this._avatarFallbackTexture(name); });
		}
	}

	// ── Layout ───────────────────────────────────────────────────────────────

	_resize(width, height) {
		this.W = Math.max(1, Math.round(width));
		this.H = Math.max(1, Math.round(height));
		this.FLOOR_Y = this.H - 8;

		if(this.app) {
			this.app.renderer.resize(this.W, this.H);
			this.bloodRenderTexture.resize(this.W, this.H);
		}

		this._buildStatics();

		this._applyAvatarPosition();
		this._applyReaperPosition();
	}

	_buildStatics() {
		if(this.walls.length) Composite.remove(this.world, this.walls);
		this.walls = [
			Bodies.rectangle(this.W / 2, this.FLOOR_Y + WALL, this.W, WALL * 2, { isStatic: true, friction: 0 }),
			Bodies.rectangle(-WALL, this.H / 2, WALL * 2, this.H * 3, { isStatic: true, friction: 0 }),
			Bodies.rectangle(this.W + WALL, this.H / 2, WALL * 2, this.H * 3, { isStatic: true, friction: 0 }),
		];
		Composite.add(this.world, this.walls);
	}

	// avatarX/avatarY (and reaperX/reaperY below) name the TOP-LEFT corner of the element's
	// nominal box, matching how every other position input on the web works — the box is
	// then centered around that corner using the current size, since sprites/text inside
	// are all built anchored to a center origin.
	_avatarPosition() {
		const s = this.settings || {};
		const avatarR = (parseFloat(s.avatarSize) || BALL_R * 2) / 2;
		return {
			x: (parseFloat(s.avatarX) || 0) + avatarR,
			y: (parseFloat(s.avatarY) || 0) + avatarR,
		};
	}

	_reaperPosition(offsetX, offsetY, reaperSize) {
		const s = this.settings || {};
		const size = reaperSize != null ? reaperSize : (parseFloat(s.reaperSize) || REAPER_SIZE);
		return {
			x: (parseFloat(s.reaperX) || 0) + size / 2 + (offsetX || 0),
			y: (parseFloat(s.reaperY) || 0) + size / 2 + (offsetY || 0),
		};
	}

	_applyAvatarPosition() {
		if(!this.activeAvatar) return;
		const p = this._avatarPosition();
		this.activeAvatar.container.x = p.x;
		this.activeAvatar.container.y = p.y;
	}

	_applyReaperPosition() {
		if(!this.activeReaper) return;
		const { offsetX, offsetY, reaperSize } = this._reaperGeometry(this.activeReaper.bannedBy);
		const p = this._reaperPosition(offsetX, offsetY, reaperSize);
		this.activeReaper.container.x = p.x;
		this.activeReaper.container.y = p.y;
	}

	_applyAvatarSize() {
		const av = this.activeAvatar;
		if(!av) return;
		const avatarR = (parseFloat(this.settings?.avatarSize) || BALL_R * 2) / 2;
		av.r = avatarR;
		av.sprite.width = av.sprite.height = avatarR * 2;
		av.mask.clear().beginFill(0xffffff).drawCircle(0, 0, avatarR).endFill();
		av.usernameText.y = avatarR + 16;
		// Anchored off the username's own rendered height (not a fixed gap from the avatar)
		// so it never overlaps regardless of font size.
		if(av.labelText) av.labelText.y = av.usernameText.y + av.usernameText.height + 4;
	}

	_applyReaperSize() {
		const m = this.activeReaper;
		if(!m) return;
		const { reaperSize } = this._reaperGeometry(m.bannedBy);
		const origSize = m.sprite.texture?.orig || { width: reaperSize, height: reaperSize };
		m.sprite.scale.set(reaperSize / Math.max(origSize.width, origSize.height));
		this._layoutReaperText(m);
	}

	// Stacked upward from the reaper's head: kill count directly above it, name above that.
	// Anchored to the current frame's actual visible (trimmed) top edge, not the nominal
	// reaperSize box — a frame with a lot of transparent padding would otherwise leave the
	// text floating far away from the character.
	_layoutReaperText(m) {
		const { top } = trimmedVerticalExtent(m.sprite);
		m.killText.y = top - 12;
		// While the kill count is hidden the name takes its place, so there's no gap where
		// the (absent) kill count would have been.
		if(m.nameText) m.nameText.y = m.killText.visible ? m.killText.y - m.killText.height - 4 : top - 12;
	}

	// ── Events / queue ───────────────────────────────────────────────────────

	onBanEvent(msg) {
		if(!this.engine) return;
		const sender = msg.sender || {};
		const name = sender.displayName || sender.username || 'chatter';
		const label = msg.type === 'timeout' ? formatDuration(msg.seconds) : null;
		const bannedBy = msg.bannedBy || null;
		const key = bannedBy ? String(bannedBy.id != null ? bannedBy.id : (bannedBy.displayName || bannedBy.username || 'unknown')) : 'unknown';
		const entry = { name, avatarUrl: sender.avatar, label, key, bannedBy };

		// Batch onto the currently-showing reaper if it's the same moderator and hasn't
		// started fading out yet; a dying session never gets revived mid-fade.
		if(this.activeReaper && this.activeReaper.key === key && this.activeReaper.state !== 'out') {
			this.activeReaper.pendingChatters.push(entry);
			return;
		}
		this.queue.push(entry);
		this._advanceQueue();
	}

	_advanceQueue() {
		if(this.activeReaper || this._startingReaper) return;
		if(!this.queue.length) return;
		const entry = this.queue.shift();
		this._startingReaper = true;
		this._startReaperSession(entry).finally(() => { this._startingReaper = false; });
	}

	async _startReaperSession(entry) {
		const { idle, slash, name } = await this._resolveReaperSheets(entry.bannedBy);
		if(!this.app) return; // destroyed while sheets were loading

		// Positioned/sized below via _applyReaperPosition/_applyReaperSize, shared with
		// the live settings-change path so a size/position tweak moves the same reaper
		// that's already on screen instead of only affecting the next one spawned.
		const sprite = new PIXI.AnimatedSprite(idle, false);
		sprite.anchor.set(0.5);

		// Carries over this moderator's running total from a previous reaper session
		// (see _killCounts) rather than always starting fresh at 0.
		const killCount = this._killCounts.get(entry.key) || 0;

		const killText = new PIXI.Text(killCount > 0 ? formatKillCount(killCount) : '', this._textStyle('700', 16, this.settings?.reaperKillCountColor || '#d81f24', this.settings?.reaperKillCountOutlineColor));
		killText.anchor.set(0.5, 1);
		killText.visible = killCount > 0; // hidden until the first kill — no point showing "0 kills"

		let nameText = null;
		if(name) {
			nameText = new PIXI.Text(name, this._textStyle('700', 17, this.settings?.reaperNameColor || '#e8e2d4', this.settings?.reaperNameOutlineColor));
			nameText.anchor.set(0.5, 1);
		}

		const container = new PIXI.Container();
		container.alpha = 0;
		container.addChild(sprite, killText);
		if(nameText) container.addChild(nameText);
		this.reaperLayer.addChild(container);

		this.activeReaper = {
			key: entry.key, name, bannedBy: entry.bannedBy, container, sprite, killText, nameText,
			idleSheet: idle, slashSheet: slash,
			killCount, opacity: 0, state: 'idle',
			pendingChatters: [], lingerUntil: 0,
			cutAt: null, cutDone: false,
		};
		this._applyReaperPosition();
		this._applyReaperSize();
		this._playIdle(this.activeReaper);

		this._showAvatar(entry);
	}

	_finishReaperSession(m) {
		this.reaperLayer.removeChild(m.container);
		m.container.destroy({ children: true, texture: false, baseTexture: false });
		this.activeReaper = null;
		this._advanceQueue();
	}

	_showAvatar(entry) {
		if(this.activeAvatar) {
			this.avatarLayer.removeChild(this.activeAvatar.container);
			this.activeAvatar.container.destroy({ children: true, texture: false, baseTexture: false });
		}

		// Positioned/sized below via _applyAvatarPosition/_applyAvatarSize, shared with
		// the live settings-change path so a size/position tweak moves the same avatar
		// that's already on screen instead of only affecting the next one spawned.
		const container = new PIXI.Container();
		const sprite = new PIXI.Sprite();
		sprite.anchor.set(0.5);
		this._applyAvatarTexture(sprite, entry.avatarUrl, entry.name);

		const mask = new PIXI.Graphics();
		sprite.mask = mask;
		container.addChild(sprite, mask);

		const usernameText = new PIXI.Text(entry.name, this._textStyle('500', 11, this.settings?.textColor || '#e8e2d4', this.settings?.textOutlineColor));
		usernameText.anchor.set(0.5, 0);
		container.addChild(usernameText);

		let labelText = null;
		if(entry.label) {
			labelText = new PIXI.Text(entry.label, this._textStyle('700', 11, '#d81f24', null));
			labelText.anchor.set(0.5, 0);
			container.addChild(labelText);
		}

		const pos = this._avatarPosition();
		container.x = pos.x; container.y = pos.y;
		container.alpha = 0;
		this.avatarLayer.addChild(container);

		this.activeAvatar = { container, sprite, mask, usernameText, labelText, r: 0, spawnedAt: performance.now() };
		this._applyAvatarSize();
	}

	// Ping-pong bounces the idle animation forward then backward instead of restarting
	// from frame 0 each loop — a cheap way to make a short clip read as having more
	// motion/frames than it actually does. Driven via loop:false + onComplete (which
	// clamps exactly to the boundary frame before firing) rather than the loop:true
	// onLoop hook — onLoop fires mid-wrap with the frame slightly past the boundary, so
	// flipping animationSpeed there overshoots back across it on the very next tick and
	// the visible frame just flickers between the first/last frame, never showing
	// anything in between (verified against AnimatedSprite's own update() math).
	_playIdle(m) {
		m.sprite.textures = m.idleSheet;
		m.sprite.animationSpeed = 1;
		if(this.settings?.idlePingPong && m.idleSheet.length > 1) {
			m.sprite.loop = false;
			m.sprite.onComplete = () => {
				m.sprite.animationSpeed *= -1;
				m.sprite.gotoAndPlay(m.sprite.currentFrame);
			};
		} else {
			m.sprite.loop = true;
			m.sprite.onComplete = null;
		}
		m.sprite.gotoAndPlay(0);
	}

	// Toggling the setting live (settings-page preview) should take effect on the reaper
	// that's already idling, not just the next one spawned. Simplest correct option is to
	// just restart idle playback under the new mode rather than patch live mid-bounce state.
	_applyIdlePingPong() {
		const m = this.activeReaper;
		if(!m || m.state !== 'idle') return;
		this._playIdle(m);
	}

	_triggerSlash(m) {
		m.state = 'slashing';
		m.sprite.textures = m.slashSheet;
		m.sprite.loop = false;
		// The idle animation may have left animationSpeed negative mid ping-pong reverse —
		// the slash always plays forward.
		m.sprite.animationSpeed = 1;
		m.sprite.onComplete = () => this._onSlashAnimationComplete(m);
		m.sprite.gotoAndPlay(0);

		// `slashCutDelay` decouples "when does the cut happen" from "when does the slash
		// animation finish" — it's ms after the slash STARTS (0 = cut immediately); left
		// blank, the cut instead fires at animation end, same as before this setting
		// existed. _onSlashAnimationComplete still cuts as a fallback if the animation
		// ends before a configured delay elapses.
		const { slashCutDelay } = this._reaperGeometry(m.bannedBy);
		m.cutAt = slashCutDelay != null ? performance.now() + Math.max(0, slashCutDelay) : null;
		m.cutDone = false;
	}

	_performCut(m) {
		if(m.cutDone) return;
		m.cutDone = true;
		if(this.activeAvatar) {
			this._cutAvatar();
			m.killCount++;
			this._killCounts.set(m.key, m.killCount);
			m.killText.text = formatKillCount(m.killCount);
			m.killText.visible = true;
			this._layoutReaperText(m); // the name shifts up once the kill count appears
		}
	}

	_onSlashAnimationComplete(m) {
		this._performCut(m);
		m.sprite.onComplete = null;
		this._playIdle(m);

		if(m.pendingChatters.length) {
			this._showAvatar(m.pendingChatters.shift());
			m.state = 'idle';
		} else {
			m.state = 'lingering';
			m.lingerUntil = performance.now() + LINGER_MS;
		}
	}

	_updateReaperState(m, now, dt) {
		const targetOpacity = m.state === 'out' ? 0 : 1;
		m.opacity += (targetOpacity - m.opacity) * Math.min(1, dt * 4);
		m.container.alpha = m.opacity;

		if(m.state === 'out') {
			if(m.opacity < 0.01) this._finishReaperSession(m);
			return;
		}

		if(m.state === 'idle') {
			const avatarReady = this.activeAvatar && (now - this.activeAvatar.spawnedAt >= CHATTER_FADE_MS);
			if(avatarReady && m.opacity > 0.95) this._triggerSlash(m);
		} else if(m.state === 'slashing') {
			if(m.cutAt != null && now >= m.cutAt) this._performCut(m);
		} else if(m.state === 'lingering') {
			if(m.pendingChatters.length) {
				this._showAvatar(m.pendingChatters.shift());
				m.state = 'idle';
			} else if(now >= m.lingerUntil) {
				m.state = 'out';
			}
		}
	}

	// ── Cut geometry / blood ─────────────────────────────────────────────────

	_cutAvatar() {
		const av = this.activeAvatar;
		if(!av) return;
		const C = this._avatarPosition();
		const r = av.r;
		const texture = av.sprite.texture;

		this.avatarLayer.removeChild(av.container);
		av.container.destroy({ children: true, texture: false, baseTexture: false });
		this.activeAvatar = null;

		const phi = Math.random() * Math.PI * 2;
		const d = (Math.random() * 2 - 1) * r * 0.55;
		const nrm = { x: Math.cos(phi), y: Math.sin(phi) };

		for(const side of [+1, -1]) {
			const worldV = segmentVerts(r, phi, d, side).map(p => ({ x: C.x + p.x, y: C.y + p.y }));
			const ctr = polyCentroid(worldV);
			const half = Bodies.fromVertices(ctr.x, ctr.y, [worldV],
				{ restitution: 0.1, friction: 0.4, frictionAir: 0.03, density: 0.0006 }, true);
			if(!half) continue;
			Composite.add(this.world, half);
			Body.setVelocity(half, {
				x: side * nrm.x * 2.6 + (Math.random() - 0.5),
				y: side * nrm.y * 2.6 - 1 - Math.random(),
			});
			Body.setAngularVelocity(half, (Math.random() - 0.5) * 0.1);

			const bodyPos = half.position;
			const center = { x: C.x - bodyPos.x, y: C.y - bodyPos.y };
			const localVerts = half.vertices.map(v => ({ x: v.x - bodyPos.x, y: v.y - bodyPos.y }));

			// UV = vertex position relative to the original circle center, normalized to [0,1] —
			// matches how the intact avatar sprite maps the same texture onto a 2r×2r box.
			const verts = [], uvs = [];
			for(const v of localVerts) {
				verts.push(v.x, v.y);
				uvs.push((v.x - center.x + r) / (2 * r), (v.y - center.y + r) / (2 * r));
			}
			const indices = [];
			for(let i = 1; i < localVerts.length - 1; i++) indices.push(0, i, i + 1);

			const mesh = new PIXI.SimpleMesh(texture, new Float32Array(verts), new Float32Array(uvs), new Uint16Array(indices));
			this.halvesLayer.addChild(mesh);

			this.halves.push({
				body: half, mesh, r, center,
				emit: { x: center.x + d * nrm.x, y: center.y + d * nrm.y },
				nrm: { x: -side * nrm.x, y: -side * nrm.y },
				bornAt: performance.now(),
				bleedStart: performance.now(),
				bleedMs: Math.max(1, this.bleedSeconds * 1000) * (0.8 + Math.random() * 0.4),
			});
		}
	}

	_getDrop(x, y) {
		let d = this.dropPool.pop();
		if(d) {
			Body.setPosition(d, { x, y });
			Body.setAngle(d, 0);
			Body.setVelocity(d, { x: 0, y: 0 });
			Body.setAngularVelocity(d, 0);
			Composite.add(this.world, d);
		} else {
			d = Bodies.circle(x, y, DROP_R, { friction: 0, frictionAir: 0.006, restitution: 0.2, density: 0.004, slop: 0.5 });
			Composite.add(this.world, d);
		}
		return d;
	}

	_freeDrop(d) {
		Composite.remove(this.world, d);
		this.dropPool.push(d);
	}

	_emitBlood(now) {
		for(const h of this.halves) {
			const life = (now - h.bleedStart) / h.bleedMs;
			if(life >= 1) continue;
			const flow = 1 - life;
			const b = h.body, c = Math.cos(b.angle), s = Math.sin(b.angle);
			const ex = b.position.x + h.emit.x * c - h.emit.y * s;
			const ey = b.position.y + h.emit.x * s + h.emit.y * c;
			const nx = h.nrm.x * c - h.nrm.y * s, ny = h.nrm.x * s + h.nrm.y * c;
			const n = (Math.random() < flow ? 1 : 0) + (Math.random() < flow * 0.7 ? 1 : 0);
			for(let i = 0; i < n; i++) {
				if(this.drops.length >= this.MAX_DROPS) this._freeDrop(this.drops.shift());
				const d = this._getDrop(ex, ey);
				Body.setVelocity(d, {
					x: nx * 1.6 + (Math.random() - 0.5) * 1.8 + b.velocity.x * 0.3,
					y: ny * 1.6 + Math.random() * 0.8 + 0.8,
				});
				d.spawnedAt = now;
				this.drops.push(d);
			}
		}
	}

	_cullDrops(now) {
		for(let i = this.drops.length - 1; i >= 0; i--) {
			const d = this.drops[i];
			const p = d.position;
			if(now - d.spawnedAt > 10000 || p.y > this.H + 80 || p.x < -80 || p.x > this.W + 80) {
				this._freeDrop(d);
				this.drops.splice(i, 1);
			}
		}
	}

	_cullHalves(now) {
		for(let i = this.halves.length - 1; i >= 0; i--) {
			if(now - this.halves[i].bornAt > 10000) {
				const removed = this.halves[i];
				Composite.remove(this.world, removed.body);
				this.halvesLayer.removeChild(removed.mesh);
				removed.mesh.destroy({ texture: false, baseTexture: false });
				this.halves.splice(i, 1);
			}
		}
	}

	_updateHalves() {
		for(const h of this.halves) {
			h.mesh.x = h.body.position.x;
			h.mesh.y = h.body.position.y;
			h.mesh.rotation = h.body.angle;
		}
	}

	_updateBloodTexture() {
		this.bloodGraphics.clear();
		if(this.drops.length) {
			this.bloodGraphics.beginFill(0xb30f15);
			for(const d of this.drops) this.bloodGraphics.drawCircle(d.position.x, d.position.y, d.circleRadius);
			this.bloodGraphics.endFill();
		}
		this.app.renderer.render(this.bloodGraphics, { renderTexture: this.bloodRenderTexture, clear: true });
	}

	// ── Loop ─────────────────────────────────────────────────────────────────

	_tick(now) {
		const frameMs = Math.min(now - this._lastTime, 100);
		const dt = frameMs / 1000;
		this._acc += frameMs;
		this._lastTime = now;

		if(this.activeAvatar) {
			this.activeAvatar.container.alpha = Math.min(1, (now - this.activeAvatar.spawnedAt) / CHATTER_FADE_MS);
		}
		if(this.activeReaper) {
			this.activeReaper.sprite.update(frameMs / (1000 / 60));
			this._updateReaperState(this.activeReaper, now, dt);
		}

		let steps = 0;
		while(this._acc >= STEP && steps < 3) {
			this._emitBlood(now);
			Engine.update(this.engine, STEP);
			this._acc -= STEP;
			steps++;
		}

		this._cullDrops(now);
		this._cullHalves(now);
		this._updateHalves();
		this._updateBloodTexture();
		this.app.renderer.render(this.app.stage);

		this._animFrameId = requestAnimationFrame(t => this._tick(t));
	}
}
